import { createStore, select, setProp, withProps } from '@ngneat/elf';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  debounceTime,
  map,
  shareReplay,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';

import { IFavoriteFilter } from './favorite-filter.model';

export interface FavoriteFiltersProps {
  favoriteFilters: IFavoriteFilter[];
  reportFavoriteFilters: IFavoriteFilter[];
  activeId: number | null;
  defaultFavoriteFilter: IFavoriteFilter | null;
  loading: boolean;
  error: HttpErrorResponse | null;
}

const initialProps: FavoriteFiltersProps = {
  favoriteFilters: [],
  reportFavoriteFilters: [],
  activeId: null,
  defaultFavoriteFilter: null,
  loading: false,
  error: null,
};

const STORE_NAME = 'sandoz-v2-favorite-filters';

@Injectable({ providedIn: 'root' })
export class FavoriteFiltersRepository {
  private readonly store;

  public readonly loading$: Observable<boolean>;
  public readonly error$: Observable<HttpErrorResponse | null>;
  public readonly defaultFavoriteFilter$: Observable<IFavoriteFilter | null>;
  public readonly activeId$: Observable<number | null>;
  public readonly favoriteFilters$: Observable<IFavoriteFilter[]>;
  public readonly reportFavoriteFilters$: Observable<IFavoriteFilter[]>;
  public readonly favoriteFiltersCount$: Observable<number>;
  public readonly activeFilter$: Observable<IFavoriteFilter | null>;

  /** Valid favorite means all the filters in it are the same as currently active filters */
  readonly isActiveFavoriteValid$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.store = createStore(
      { name: STORE_NAME },
      withProps<FavoriteFiltersProps>(initialProps),
    );

    // need to persist state, otherwise the filters get messed up after refreshing the page
    persistState(this.store, {
      key: STORE_NAME,
      storage: localStorageStrategy,
    });

    this.loading$ = this.store.pipe(
      select(state => state.loading),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.error$ = this.store.pipe(
      select(state => state.error),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.defaultFavoriteFilter$ = this.store.pipe(
      select(state => state.defaultFavoriteFilter),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.activeId$ = this.store.pipe(
      select(state => state.activeId),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.favoriteFilters$ = this.store.pipe(
      select(state => state.favoriteFilters),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.reportFavoriteFilters$ = this.store.pipe(
      select(state => state.reportFavoriteFilters),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.favoriteFiltersCount$ = this.store.pipe(
      select(state => state.favoriteFilters.length),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.activeFilter$ = combineLatest([
      this.activeId$,
      this.favoriteFilters$,
    ]).pipe(
      debounceTime(0),
      map(([activeId, filters]) => {
        if (!activeId) return null;
        const filter = filters.find(filter => filter.id === activeId) || null;

        return filter;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  resetStore(): void {
    const defaultFavoriteFilter = this.getStoreProp('defaultFavoriteFilter');
    this.store.reset();
    this.setDefaultFavoriteFilter(defaultFavoriteFilter);
  }

  remove(id: number): void {
    const currentFilters = this.getFavoriteFilters();
    const updatedFilters = currentFilters.filter(f => f.id !== id);
    this.setFavoriteFilters(updatedFilters);
  }

  removeReport(id: number): void {
    const currentReports = this.getReportFavoriteFilters();
    const updatedReports = currentReports.filter(f => f.id !== id);
    this.setReportFavoriteFilters(updatedReports);
  }

  getStoreProp<K extends keyof FavoriteFiltersProps>(
    key: K,
  ): FavoriteFiltersProps[K] {
    return this.store.getValue()[key];
  }

  getFavoriteFilters(): IFavoriteFilter[] {
    return this.store.getValue().favoriteFilters;
  }

  getReportFavoriteFilters(): IFavoriteFilter[] {
    return this.store.getValue().reportFavoriteFilters;
  }

  getActiveFilter(): IFavoriteFilter | null {
    const state = this.store.getValue();
    const activeId = state.activeId;

    if (!activeId) return null;

    return state.favoriteFilters.find(filter => filter.id === activeId) || null;
  }

  setFavoriteFilters(filters: IFavoriteFilter[]): void {
    const validFilters = filters.filter(
      filter => filter.id !== null && filter.id !== undefined,
    );
    this.store.update(setProp('favoriteFilters', validFilters));
  }

  setReportFavoriteFilters(filters: IFavoriteFilter[]): void {
    const validFilters = filters.filter(
      filter => filter.id !== null && filter.id !== undefined,
    );
    this.store.update(setProp('reportFavoriteFilters', validFilters));
  }

  setLoading(loading: boolean): void {
    this.store.update(setProp('loading', loading));
  }

  setError(error: HttpErrorResponse | null): void {
    this.store.update(setProp('error', error));
  }

  setDefaultFavoriteFilter(filter: IFavoriteFilter | null): void {
    this.store.update(setProp('defaultFavoriteFilter', filter));
  }

  setActiveId(id: number | null): void {
    this.store.update(setProp('activeId', id));
  }
}
